import React, { useEffect, useState, useCallback } from "react";
import { View, StyleSheet, Platform } from "react-native";
import CustomText from "../components/common/general/CustomText/CustomText";
import CustomSpacing from "../components/common/layout/CustomSpacing/CustomSpacing";
import { useRoute, useFocusEffect, RouteProp } from "@react-navigation/native";
import { MotiView } from "moti";
import { searchWeb } from "../services";
import FileCard from "../components/lms/FileCard/FileCard";
import useResponsiveScreen from "../hooks/useResponsiveScreen";
import CustomHtml from "../components/common/general/CustomHtml/CustomHtml";
import SourcesList from "../components/lms/SourcesList/SourcesList";
import ImageCardList from "../components/lms/ImageCardList/ImageCardList";
import VideoCardList from "../components/lms/VideoCardList/VideoCardList";
import Divider from "../components/common/layout/Divider/Divider";
import Loader from "../components/common/status/Loader/Loader";
import { useAppConfig } from "../AppConfigProvider";
import Sentry from "../utils/sentry";

import { useAppState } from "../contexts/AppStateContext";
import { getValidAccessToken } from "../utils/authRefresh";
import ActivityInstructionsModal from "../components/lms/ActivityInstructionsModal/ActivityInstructionsModal";
import type { RootStackParamList } from "../navigation/navigation.types";
import DefaultLayout from "../layouts/DefaultLayout.web";
import { ResourceType, useActiveCourse } from "../contexts/ActiveCourseContext";
import WebView from "react-native-webview";
import { getEmbeddedVideoUrl } from "../utils/regex.dictionary";
import useFileFetch from "../hooks/useFileFetch";

function CustomResourceStudentView() {
  const [isLoadingSidebarData, setIsLoadingSidebarData] = useState(true);
  const [showVideo, setShowVideo] = useState(true);

  const { fetchAndOpenFile } = useFileFetch();

  const route = useRoute<RouteProp<RootStackParamList, "Custom Resource Student">>();

  const {
    resource_type: resourceType,
    resource_id: resourceId,
    course_id: courseId,
    integration_id: integrationId,
  } = route.params;

  const { isMedium } = useResponsiveScreen();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const appConfig = useAppConfig();
  const isSuggestedInsights = appConfig.isSuggestedInsights;

  const [videoCardData, setVideoCardData] = useState([]);
  const [sources, setSources] = useState([]);
  const [imageCardData, setImageCardData] = useState([]);

  const { dispatch } = useAppState();

  const { axioResourceData, setActiveResource } = useActiveCourse();
  const resourceName = axioResourceData?.getAxioResource?.data?.[0]?.name;

  useEffect(() => {
    setActiveResource({ resourceType: resourceType as ResourceType, resourceId });
  }, [resourceType, resourceId]);

  useFocusEffect(
    useCallback(() => {
      // Reset media state when the component is focused
      dispatch({
        type: "SET_MEDIA",
        payload: {
          video: null,
          wiki: null,
        },
      });
      setShowVideo(true);

      return () => {
        setShowVideo(false);
        dispatch({
          type: "SET_MEDIA",
          payload: {
            video: null,
            wiki: null,
          },
        });
      };
    }, [dispatch])
  );

  useEffect(() => {
    if (resourceType === "axioResource" && axioResourceData?.getAxioResource?.data?.[0].name) {
      const id = axioResourceData?.getAxioResource?.data?.[0].id;
      const fetchData = async () => {
        try {
          setIsLoadingSidebarData(true);
          const token = await getValidAccessToken();
          const data = await searchWeb(id, token);

          const videos = data.video.map((item) => ({
            imageUrl: item.metadata.videoThumbnail,
            title: item.metadata.title,
            url: item.metadata.source,
          }));

          const images = data.images.flatMap((item) =>
            item.metadata.images.slice(0, 1).map((image) => ({
              imageUrl: image,
              title: item.metadata.title,
              source: item.metadata.source,
            }))
          );

          const wikipedia = data.Wikipedia.map((item) => ({
            content: item.pageContent,
            title: item.metadata.title,
            source: item.metadata.source,
          }));

          setVideoCardData(videos);
          setImageCardData(images);
          setSources(wikipedia);
          setIsLoadingSidebarData(false);
        } catch (error) {
          Sentry.captureException(error);
          setIsLoadingSidebarData(false);
        }
      };

      fetchData();
    }
  }, [axioResourceData, resourceType]);

  const renderFiles = (files) => {
    if (!files || files.length === 0) return null;

    return (
      <>
        <CustomText text="Files" size="m" weight="bold" aria-level={2} role="heading" accessibilityRole="header" />
        <CustomSpacing type="vertical" size="s" />
        <View style={styles.filesContainer} role="list" accessibilityRole="list">
          {files.map((file) => (
            <View key={file.fileId || file.id} role="listitem" accessibilityRole="listitem">
              <FileCard title={file.fileName} url={file.fileURL} onPress={() => fetchAndOpenFile(file.fileURL)} />
            </View>
          ))}
        </View>
        <CustomSpacing type="vertical" size="xl" />
      </>
    );
  };

  const renderVideo = () => {
    const videoUrl = axioResourceData?.getAxioResource?.data?.[0].videoUrl;
    const embeddedVideoUrl = getEmbeddedVideoUrl(videoUrl);
    if (embeddedVideoUrl && showVideo) {
      return (
        <View
          style={{
            width: "30%",
            marginRight: 10,
            marginBottom: 10,
            aspectRatio: 16 / 9,
            borderRadius: 8,
            overflow: "hidden",
          }}
          role="region"
          aria-label="Resource Video"
          accessibilityLabel="Resource Video"
          accessibilityRole="none">
          <WebView
            source={{ uri: embeddedVideoUrl }}
            javaScriptEnabled
            domStorageEnabled
            allowsFullscreenVideo
            style={{ flex: 1 }}
            accessibilityLabel="Resource Video"
            title="Resource Video"
          />
        </View>
      );
    }
  };

  const renderSidebar = () => {
    const hasData = sources.length > 0 || imageCardData.length > 0 || videoCardData.length > 0;

    if (!hasData) {
      return null;
    }

    return (
      <MotiView
        from={{ opacity: 0, translateY: 20, scale: 1 }}
        animate={{ opacity: 1, translateY: 0, scale: 1 }}
        delay={10}
        style={styles.sidebarContainer}
        transition={{ type: "timing", duration: 600 }}>
        {sources.length > 0 && (
          <>
            <CustomText
              text="Sources"
              style={styles.headerText}
              size="m"
              weight="bold"
              role="heading"
              aria-level={2}
              accessibilityRole="header"
            />
            <SourcesList sources={sources} />
            <CustomSpacing type="vertical" size="m" />
          </>
        )}

        {imageCardData.length > 0 && (
          <>
            <CustomText
              text="Images"
              style={styles.headerText}
              size="m"
              weight="bold"
              role="heading"
              aria-level={2}
              accessibilityRole="header"
            />
            <ImageCardList imageCardData={imageCardData} />
            <CustomSpacing type="vertical" size="m" />
          </>
        )}

        {videoCardData.length > 0 && (
          <>
            <CustomText
              text="Videos"
              style={styles.headerText}
              size="m"
              weight="bold"
              role="heading"
              aria-level={2}
              accessibilityRole="header"
            />
            <VideoCardList videoCardData={videoCardData} />
            <CustomSpacing type="vertical" size="m" />
          </>
        )}
      </MotiView>
    );
  };

  return (
    <DefaultLayout showCourseNav={true} pageHasChat={false}>
      <ActivityInstructionsModal visible={isModalVisible} onClose={() => setIsModalVisible(false)} />

      <View style={isMedium ? styles.mainContainer : styles.mainContainerMobile}>
        <View style={isMedium ? styles.rowContainer : styles.columnContainer}>
          <View style={isMedium ? styles.contentContainer : styles.fullWidthContentContainer}>
            <MotiView
              from={{
                opacity: 0,
                translateY: 20,
                scale: 1,
              }}
              animate={{
                opacity: 1,
                translateY: 0,
                scale: 1,
              }}
              delay={600}
              transition={{ type: "timing", duration: 600 }}
              style={{ paddingHorizontal: 30, paddingVertical: 10 }}>
              {resourceName && (
                <CustomText
                  text={resourceName}
                  useTranslationText={false}
                  size="l"
                  weight="bold"
                  role="heading"
                  aria-level={1}
                  accessibilityRole="header"
                />
              )}
              <CustomText
                text="Description"
                size="m"
                weight="bold"
                aria-level={2}
                role="heading"
                accessibilityRole="header"
              />
              <CustomSpacing type="vertical" size="s" />
              {resourceType === "axioResource" && axioResourceData?.getAxioResource?.data?.[0].description && (
                <CustomHtml html={axioResourceData.getAxioResource.data[0].description} allowEmbeds />
              )}

              <CustomSpacing type="vertical" size="xl" />

              {/* Files Section */}
              {renderFiles(axioResourceData?.getAxioResource?.data?.[0].files)}

              {/* Video URL Section */}
              {resourceType === "axioResource" && axioResourceData?.getAxioResource?.data?.[0].videoUrl && (
                <>
                  <CustomText
                    text="Video"
                    size="m"
                    weight="bold"
                    aria-level={2}
                    role="heading"
                    accessibilityRole="header"
                  />
                  <CustomSpacing type="vertical" size="s" />
                  {renderVideo()}
                  <CustomSpacing type="vertical" size="xl" />
                </>
              )}
            </MotiView>

            <CustomSpacing type="vertical" size="l" />
          </View>

          {/* Sidebar Section */}
          {isSuggestedInsights && (
            <>
              {isMedium ? (
                <View style={[{ width: 300 }, isLoadingSidebarData ? { alignItems: "center" } : {}]}>
                  <CustomText
                    style={{ margin: 12 }}
                    textType="display"
                    text="Suggested Insights"
                    size="xs"
                    bold={false}
                    role="heading"
                    accessibilityRole="header"
                    aria-level={2}
                  />
                  {isLoadingSidebarData ? (
                    <View style={styles.insightsCenterView}>
                      <Loader />
                    </View>
                  ) : (
                    <>{renderSidebar()}</>
                  )}
                </View>
              ) : (
                <>
                  <Divider size="l" />
                  <CustomText
                    textType="display"
                    text="Suggested Insights"
                    size="xs"
                    bold={true}
                    weight="bold"
                    role="heading"
                    accessibilityRole="header"
                    aria-level={2}
                  />
                  <View style={isLoadingSidebarData ? { alignItems: "center" } : {}}>
                    {isLoadingSidebarData ? (
                      <View style={styles.fullWidthContentContainer}>
                        <Loader />
                      </View>
                    ) : (
                      <>{renderSidebar()}</>
                    )}
                  </View>
                </>
              )}
            </>
          )}
        </View>
      </View>
    </DefaultLayout>
  );
}

const styles = StyleSheet.create({
  mainContainerMobile: {
    height: 20,
  },
  mainContainer: {
    // flex: 1,
    height: 20,
  },
  rowContainer: {
    flexDirection: "row",
    flex: 1,
  },
  columnContainer: {
    flexDirection: "column",
    flex: 1,
  },
  fullWidthContentContainer: {
    padding: 16,
    // height: 20,
  },
  contentContainer: {
    flex: 3, // Takes 3/4 of the width
    padding: 16,
  },
  headerText: {
    marginBottom: 6,
  },
  sidebarContainer: {
    flex: 1, // Takes 1/4 of the width
    justifyContent: "flex-start",
    paddingBottom: 16,
    paddingLeft: 16,
    paddingRight: 16,
  },
  insightsCenterView: {
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  filesContainer: {
    flexDirection: "column",
    gap: 10,
    maxWidth: 320,
  },
});

export default CustomResourceStudentView;
